/**
 * Purchase Order / Delivery Order queries — PRD.192.
 *
 * Boundary: PO save and DO draft save do NOT write to stock_movements.
 * Only DO finalize writes inbound rows with source_type='delivery_order'
 * and quantity_in > 0. The data-access layer only exposes reads.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface PurchaseOrderRow {
  id: string;
  org_id: string | null;
  po_number: string | null;
  po_date: string | null;
  // PRD.207 — link to inventory_suppliers (nullable for legacy free-text rows).
  supplier_id: string | null;
  supplier_name: string | null;
  amounts_are: string | null; // 'tax_exclusive' | 'tax_inclusive'
  notes: string | null;
  status: string | null; // 'draft' | 'posted' | 'cancelled'
  status_executor: string | null;
  total_amount: number | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PurchaseOrderLineRow {
  id: string;
  org_id: string | null;
  purchase_order_id: string | null;
  inventory_item_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  quantity_ordered: number | null;
  bonus_quantity: number | null;
  rate: number | null;
  amount: number | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeliveryOrderRow {
  id: string;
  org_id: string | null;
  do_number: string | null;
  purchase_order_id: string | null;
  // PRD.265 — 'from_purchase_order' | 'manual_direct_receipt'.
  source_type: string | null;
  // PRD.207 — link to inventory_suppliers (nullable for legacy free-text rows).
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_invoice_number: string | null;
  reference: string | null;
  date_raised: string | null;
  delivery_date: string | null;
  received_date: string | null;
  amounts_are: string | null;
  subtotal: number | null;
  // PRD.265 — tax capture.
  tax_rate: number | null;
  tax_amount: number | null;
  invoice_total: number | null;
  notes: string | null;
  status: string | null; // 'draft' | 'received' | 'cancelled'
  received_by: string | null;
  // PRD.265 — cancellation audit.
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DeliveryOrderLineRow {
  id: string;
  org_id: string | null;
  delivery_order_id: string | null;
  purchase_order_line_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  unit_snapshot: string | null;
  purchasing_unit_ordered: string | null;
  quantity_ordered: number | null;
  quantity_received: number | null;
  bonus_quantity_received: number | null;
  unit_price: number | null;
  unit_price_without_bonus: number | null;
  rate: number | null;
  amount: number | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qPurchaseOrdersList(): QuerySpec<[]> {
  return {
    key: ['purchase-orders-list'] as const,
    sql: `
      SELECT * FROM purchase_orders
      ORDER BY po_date DESC, created_at DESC
    `,
    params: [],
    watch: ['purchase_orders'],
  };
}

export function qPurchaseOrderLinesByOrder(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['purchase-order-lines', purchaseOrderId] as const,
    sql: `
      SELECT * FROM purchase_order_lines
      WHERE purchase_order_id = ?
      ORDER BY created_at ASC
    `,
    params: [purchaseOrderId],
    watch: ['purchase_order_lines'],
  };
}

/**
 * Read a single PO header by id, returning at most one row. Useful when
 * preloading a Receive Delivery Order page from a PO link.
 */
export function qPurchaseOrderForReceive(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['purchase-order-for-receive', purchaseOrderId] as const,
    sql: `SELECT * FROM purchase_orders WHERE id = ? LIMIT 1`,
    params: [purchaseOrderId],
    watch: ['purchase_orders'],
  };
}

export function qDeliveryOrdersList(): QuerySpec<[]> {
  return {
    key: ['delivery-orders-list'] as const,
    sql: `
      SELECT * FROM delivery_orders
      ORDER BY received_date DESC, delivery_date DESC, created_at DESC
    `,
    params: [],
    watch: ['delivery_orders'],
  };
}

export function qDeliveryOrderLinesByOrder(deliveryOrderId: string): QuerySpec<[string]> {
  return {
    key: ['delivery-order-lines', deliveryOrderId] as const,
    sql: `
      SELECT * FROM delivery_order_lines
      WHERE delivery_order_id = ?
      ORDER BY created_at ASC
    `,
    params: [deliveryOrderId],
    watch: ['delivery_order_lines'],
  };
}

export function qDeliveryOrdersByPurchaseOrder(purchaseOrderId: string): QuerySpec<[string]> {
  return {
    key: ['delivery-orders-by-po', purchaseOrderId] as const,
    sql: `
      SELECT * FROM delivery_orders
      WHERE purchase_order_id = ?
      ORDER BY created_at DESC
    `,
    params: [purchaseOrderId],
    watch: ['delivery_orders'],
  };
}

/**
 * Inbound stock movements created by a specific Delivery Order. Used to
 * verify a finalize wrote ledger rows (e.g. for the DO detail view).
 */
export function qDeliveryOrderStockMovements(deliveryOrderId: string): QuerySpec<[string]> {
  return {
    key: ['stock-movements-by-do', deliveryOrderId] as const,
    sql: `
      SELECT * FROM stock_movements
      WHERE source_type = 'delivery_order' AND source_id = ?
      ORDER BY created_at ASC
    `,
    params: [deliveryOrderId],
    watch: ['stock_movements'],
  };
}

/** A single Delivery Order header by id. */
export function qDeliveryOrderById(deliveryOrderId: string): QuerySpec<[string]> {
  return {
    key: ['delivery-order', deliveryOrderId] as const,
    sql: `SELECT * FROM delivery_orders WHERE id = ? LIMIT 1`,
    params: [deliveryOrderId],
    watch: ['delivery_orders'],
  };
}

/** Receivable inventory item row for the direct-receipt picker (PRD.265). */
export interface ReceivableItemRow {
  id: string;
  item_name: string | null;
  sku: string | null;
  item_type: string | null;
  inventory_unit: string | null;
  purchasing_unit: string | null;
  batch_tracked: number | null;
  cost_price: number | null;
}

/**
 * PRD.265/266 — physical, stock-tracked items eligible for direct receipt:
 * medicine / product / supply / vaccination with track_stock = true. Excludes
 * services, packages, injections, and non-stock items. The item-type list
 * mirrors `RECEIVABLE_ITEM_TYPES` in item-type-behavior.ts.
 */
export function qReceivableInventoryItems(orgId: string, search: string): QuerySpec<unknown[]> {
  const term = `%${search.trim().toLowerCase()}%`;
  // NOTE: no org_id filter — PowerSync sync rules already scope the local DB to
  // the active org, matching qInventoryPickerCandidates. `track_stock` defaults
  // to true, so treat null/legacy rows as tracked and only drop explicit 0s.
  return {
    key: ['receivable-inventory-items', orgId, search] as const,
    sql: `
      SELECT
        i.id, i.item_name, i.sku, i.item_type, i.inventory_unit,
        i.purchasing_unit, i.batch_tracked, i.cost_price
      FROM inventory_items i
      WHERE i.item_type IN ('medicine', 'product', 'supply', 'vaccination')
        AND COALESCE(i.track_stock, 1) = 1
        AND COALESCE(i.inactive, 0) = 0
        AND COALESCE(i.status, 'Active') != 'Inactive'
        AND (
          ? = '%%'
          OR LOWER(COALESCE(i.item_name, '')) LIKE ?
          OR LOWER(COALESCE(i.sku, ''))       LIKE ?
        )
      ORDER BY i.item_name ASC
      LIMIT 50
    `,
    params: [term, term, term],
    watch: ['inventory_items'],
  };
}
